/**
 * CMS: reviews op /reviews (KV). Alleen superadmin (AdminGuard).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { api } from '../../lib/api';
import type { CmsReviewItem, ReviewsCmsPayload } from '../../types/cms-reviews';

function emptyReview(sort: number): CmsReviewItem {
  return {
    id: `r_${Date.now()}_${sort}`,
    quote: 'Vervang deze tekst door een echte review (met toestemming van de schrijver).',
    name: 'Voorbeeld Naam',
    role: 'Functie · Bedrijfsnaam',
    avatar_url: 'https://i.pravatar.cc/160?img=5',
    sort_order: sort,
  };
}

export function AdminCmsReviews() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-cms-reviews'],
    queryFn: () => api.getAdminCmsReviews(),
  });

  const [heading, setHeading] = useState('');
  const [lead, setLead] = useState('');
  const [reviews, setReviews] = useState<CmsReviewItem[]>([]);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    if (!data?.reviews_cms) return;
    const c = data.reviews_cms;
    setHeading(c.intro_heading);
    setLead(c.intro_lead);
    setReviews(c.reviews.map((r) => ({ ...r })));
  }, [data]);

  const mut = useMutation({
    mutationFn: (payload: ReviewsCmsPayload) => api.putAdminCmsReviews(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-cms-reviews'] });
      void qc.invalidateQueries({ queryKey: ['public-cms-reviews'] });
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 4000);
    },
  });

  function reorder(next: CmsReviewItem[]) {
    setReviews(next.map((r, i) => ({ ...r, sort_order: i })));
  }

  const payload: ReviewsCmsPayload | null =
    heading.trim() && lead.trim() && reviews.length > 0
      ? {
          schema_version: 1,
          intro_heading: heading.trim(),
          intro_lead: lead.trim(),
          reviews,
          updated_at: data?.reviews_cms?.updated_at ?? new Date().toISOString(),
        }
      : null;

  return (
    <div>
      <SeoMeta
        path="/admin/cms/reviews"
        noindex
        title="CMS — Reviews"
        description="Beheer publieke reviewpagina CalqTool — alleen superadmin."
      />
      <Breadcrumbs
        items={[
          { label: 'Platformbeheer', href: '/admin' },
          { label: 'CMS', href: '/admin/cms' },
          { label: 'Reviews' },
        ]}
      />
      <h1 className="mb-2 text-2xl font-bold text-text">CMS — Reviews</h1>
      <p className="mb-2 max-w-2xl text-sm text-text-muted">
        Dit vult de publieke pagina{' '}
        <Link to="/reviews" className="font-medium text-primary hover:underline" target="_blank" rel="noreferrer">
          Reviews
        </Link>
        . Heb je nog niet opgeslagen? Bezoekers zien dan de ingebouwde voorbeeldteksten. Na <strong>Opslaan</strong>{' '}
        gebruikt de site jouw kop, intro en kaarten.
      </p>
      {data?.stored === false && (
        <p className="mb-6 rounded-lg border border-sky/40 bg-sky/10 px-3 py-2 text-sm text-text">
          Je bewerkt nu de <strong>standaardvoorbeelden</strong>. Klik op Opslaan om ze live te zetten.
        </p>
      )}

      {isLoading ? (
        <p className="text-sm text-text-muted">Laden…</p>
      ) : (
        <form
          className="max-w-3xl space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            if (!payload) return;
            void mut.mutateAsync(payload);
          }}
        >
          <div className="rounded-xl border border-border bg-surface-card p-5 space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-text">Kop (H1)</label>
              <input
                value={heading}
                onChange={(e) => setHeading(e.target.value)}
                maxLength={200}
                className="w-full rounded-lg border border-border px-3 py-2 text-text"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-text">Intro (onder kop)</label>
              <p className="mb-2 text-xs text-text-muted">
                Toon voor bezoekers: warm en duidelijk. Geen interne termen; geen uitleg dat jij de pagina beheert.
              </p>
              <textarea
                value={lead}
                onChange={(e) => setLead(e.target.value)}
                rows={4}
                maxLength={1200}
                className="w-full rounded-lg border border-border px-3 py-2 text-text"
              />
              <p className="mt-1 text-xs text-text-muted">{lead.length}/1200</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-text">Reviewkaarten</h2>
              <button
                type="button"
                onClick={() => reorder([...reviews, emptyReview(reviews.length)])}
                className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface-card px-3 py-2 text-sm font-medium text-text hover:bg-surface"
              >
                <Plus className="h-4 w-4" />
                Review toevoegen
              </button>
            </div>

            {reviews.map((r, idx) => (
              <div key={r.id} className="rounded-xl border border-border bg-surface-card p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Review {idx + 1}</span>
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      title="Omhoog"
                      disabled={idx === 0}
                      onClick={() => {
                        const n = [...reviews];
                        [n[idx - 1], n[idx]] = [n[idx], n[idx - 1]];
                        reorder(n);
                      }}
                      className="rounded-md border border-border p-1.5 text-text-muted hover:bg-surface disabled:opacity-40"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      title="Omlaag"
                      disabled={idx === reviews.length - 1}
                      onClick={() => {
                        const n = [...reviews];
                        [n[idx], n[idx + 1]] = [n[idx + 1], n[idx]];
                        reorder(n);
                      }}
                      className="rounded-md border border-border p-1.5 text-text-muted hover:bg-surface disabled:opacity-40"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      title="Verwijderen"
                      onClick={() => reorder(reviews.filter((_, i) => i !== idx))}
                      className="rounded-md border border-red-200 p-1.5 text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-text-muted">Citaat</label>
                  <textarea
                    value={r.quote}
                    onChange={(e) => {
                      const v = e.target.value.slice(0, 900);
                      setReviews((prev) => prev.map((x, i) => (i === idx ? { ...x, quote: v } : x)));
                    }}
                    rows={3}
                    className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text"
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-text-muted">Naam</label>
                    <input
                      value={r.name}
                      onChange={(e) => {
                        const v = e.target.value.slice(0, 120);
                        setReviews((prev) => prev.map((x, i) => (i === idx ? { ...x, name: v } : x)));
                      }}
                      className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-text-muted">Functie · bedrijf</label>
                    <input
                      value={r.role}
                      onChange={(e) => {
                        const v = e.target.value.slice(0, 200);
                        setReviews((prev) => prev.map((x, i) => (i === idx ? { ...x, role: v } : x)));
                      }}
                      className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-text-muted">
                    Profielfoto-URL (https of pad op /)
                  </label>
                  <input
                    value={r.avatar_url}
                    onChange={(e) => {
                      const v = e.target.value.slice(0, 500);
                      setReviews((prev) => prev.map((x, i) => (i === idx ? { ...x, avatar_url: v } : x)));
                    }}
                    className="w-full rounded-lg border border-border px-3 py-2 font-mono text-xs text-text"
                    placeholder="https://…"
                  />
                </div>
              </div>
            ))}
          </div>

          {mut.isError && (
            <p className="text-sm text-red-700 dark:text-red-300">
              {mut.error instanceof Error ? mut.error.message : 'Opslaan mislukt'}
            </p>
          )}
          {savedFlash && (
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              Opgeslagen. Bezoekers zien de wijziging meestal binnen ongeveer een minuut.
            </p>
          )}

          <button
            type="submit"
            disabled={mut.isPending || !payload}
            className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-alt disabled:opacity-50"
          >
            {mut.isPending ? 'Opslaan…' : 'Opslaan naar CMS'}
          </button>
        </form>
      )}
    </div>
  );
}
